// Theme layer for the web page: the theme's font, paper colour and grain (CSS in index.html
// and fonts.css read these variables) and its drawn background, laid over everything with a
// soft blend so it reads as printed on the page: constructivist shapes, a frame of Islamic
// geometric ornaments in the corners, or Matrix rain. Never takes touches.
import { useEffect } from 'react';
import { usePalette, type Palette } from '../theme';

const svgUrl = (svg: string) => `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;

/** A poster composition: a big circle, crossing bars, a wedge, a square. Fills the screen. */
function constructivist(p: Palette): string {
  const red = p.tint, black = p.text, ochre = p.warning;
  return svgUrl(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 800' preserveAspectRatio='xMidYMid slice'>
    <circle cx='340' cy='110' r='120' fill='${red}'/>
    <rect x='-80' y='430' width='620' height='42' fill='${black}' transform='rotate(-28 200 450)'/>
    <rect x='-80' y='500' width='620' height='10' fill='${red}' transform='rotate(-28 200 450)'/>
    <polygon points='0,800 0,590 190,800' fill='${ochre}'/>
    <rect x='300' y='640' width='70' height='70' fill='${black}' transform='rotate(12 335 675)'/>
    <circle cx='60' cy='300' r='26' fill='none' stroke='${black}' stroke-width='6'/>
    <line x1='0' y1='210' x2='400' y2='150' stroke='${black}' stroke-width='3'/>
  </svg>`);
}

/** Star polygon {n/k} points on radius r (e.g. 10/3: the girih star). */
const starPoints = (n: number, r: number, inner: number) =>
  Array.from({ length: 2 * n }, (_, i) => {
    const a = (i * Math.PI) / n;
    const d = i % 2 ? r * inner : r;
    return `${(Math.sin(a) * d).toFixed(1)},${(-Math.cos(a) * d).toFixed(1)}`;
  }).join(' ');

const C = 120; // corner ornament size (px)
const LINE = 9; // where the frame lines run: in the page margin, clear of the content

/**
 * A corner ornament from traditional Islamic and Persian geometric art, drawn for the top-left
 * corner: one khatam star (two interlaced squares) where the frame lines meet, and smaller
 * motifs bunched beside it on the frame (shamseh rosettes, girih stars, dots), mirror-symmetric across
 * the corner's diagonal. `flipX`/`flipY` mirror it into the other corners.
 */
function corner(p: Palette, flipX: boolean, flipY: boolean): string {
  const green = p.tint, gold = p.super;
  const line = (c: string, w = 1.6) => `fill='none' stroke='${c}' stroke-width='${w}' stroke-linejoin='round'`;
  const khatam = (x: number, y: number, r: number, c: string) => {
    const sq = `<rect x='${-r * 0.71}' y='${-r * 0.71}' width='${r * 1.42}' height='${r * 1.42}'/>`;
    return `<g transform='translate(${x} ${y})' ${line(c)}><circle r='${r * 1.1}' fill='${p.background}' stroke-width='0.9'/>${sq}<g transform='rotate(45)'>${sq}</g><polygon points='${starPoints(8, r * 0.48, 0.62)}' stroke-width='1.1'/><circle r='${r * 0.12}' fill='${c}'/></g>`;
  };
  const shamseh = (x: number, y: number, r: number, c: string) =>
    `<g transform='translate(${x} ${y})' ${line(c, 1.3)}><polygon points='${starPoints(12, r, 0.72)}'/><circle r='${r * 0.45}'/></g>`;
  const girih = (x: number, y: number, r: number, c: string) =>
    `<g transform='translate(${x} ${y}) rotate(18)' ${line(c, 1.3)}><polygon points='${starPoints(10, r, 0.6)}'/></g>`;
  const dot = (x: number, y: number, r: number, c: string) => `<circle cx='${x}' cy='${y}' r='${r}' fill='${c}'/>`;
  // Pairs mirrored across the diagonal (x↔y), set on the frame lines so they stay in the margin.
  const pair = (f: (x: number, y: number) => string, x: number, y: number) => f(x, y) + f(y, x);
  const L = LINE;
  const art = [
    khatam(L + 4, L + 4, 15, gold),
    pair((x, y) => shamseh(x, y, 5, green), L + 27, L),
    pair((x, y) => girih(x, y, 6, gold), L + 42, L),
    pair((x, y) => girih(x, y, 4, green), L + 57, L),
    pair((x, y) => dot(x, y, 1.8, gold), L + 69, L),
  ].join('');
  const flip = `translate(${flipX ? C : 0} ${flipY ? C : 0}) scale(${flipX ? -1 : 1} ${flipY ? -1 : 1})`;
  return svgUrl(`<svg xmlns='http://www.w3.org/2000/svg' width='${C}' height='${C}'><g transform='${flip}'>${art}</g></svg>`);
}

/** The Oriental frame: an ornament in each corner, joined by fine double lines like a card's border. */
function orientalFrame(p: Palette): HTMLElement {
  const frame = document.createElement('div');
  Object.assign(frame.style, {
    position: 'fixed', inset: '0', pointerEvents: 'none', zIndex: '2147483646',
    opacity: p.scheme === 'dark' ? '0.35' : '0.45', mixBlendMode: p.scheme === 'dark' ? 'screen' : 'multiply',
  });
  for (const [inset, width, color] of [[LINE - 2, 1, p.tint], [LINE + 2, 1, p.super]] as const) {
    const rule = document.createElement('div');
    Object.assign(rule.style, { position: 'absolute', inset: `${inset}px`, border: `${width}px solid ${color}`, borderRadius: '2px' });
    frame.appendChild(rule);
  }
  const corners = document.createElement('div');
  Object.assign(corners.style, {
    position: 'absolute', inset: '0',
    backgroundImage: [corner(p, false, false), corner(p, true, false), corner(p, false, true), corner(p, true, true)].join(','),
    backgroundPosition: 'left top, right top, left bottom, right bottom',
    backgroundRepeat: 'no-repeat',
    backgroundSize: `${C}px ${C}px`,
  });
  frame.appendChild(corners);
  return frame;
}

const RAIN = 'アイウエオカキクケコサシスセソタチツテトナニヌネノ0123456789ハヒフヘホマミムメモヤユヨラリルレロワン';

/** Falling code on a canvas; a still frame when the system asks for reduced motion. */
function matrixRain(color: string): () => void {
  const canvas = document.createElement('canvas');
  Object.assign(canvas.style, { position: 'fixed', inset: '0', width: '100%', height: '100%', pointerEvents: 'none', zIndex: '2147483646', opacity: '0.16', mixBlendMode: 'screen' });
  canvas.setAttribute('aria-hidden', 'true');
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d')!;
  const size = 16;
  let drops: number[] = [];
  const resize = () => {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    drops = Array.from({ length: Math.ceil(canvas.width / size) }, () => Math.floor(Math.random() * -60));
  };
  resize();
  window.addEventListener('resize', resize);
  const frame = () => {
    ctx.fillStyle = 'rgba(0,0,0,0.12)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = color;
    ctx.font = `${size}px ChefiMatrix, monospace`;
    drops.forEach((y, i) => {
      if (y >= 0) ctx.fillText(RAIN[Math.floor(Math.random() * RAIN.length)], i * size, y * size);
      drops[i] = y * size > canvas.height && Math.random() > 0.975 ? 0 : y + 1;
    });
  };
  const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  let timer: ReturnType<typeof setInterval> | undefined;
  if (still) for (let i = 0; i < 80; i++) frame();
  else timer = setInterval(frame, 70);
  return () => {
    if (timer) clearInterval(timer);
    window.removeEventListener('resize', resize);
    canvas.remove();
  };
}

export default function ThemeLayer() {
  const p = usePalette();
  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty('--grain', String(p.grain));
    root.setProperty('--grain-blend', p.scheme === 'dark' ? 'screen' : 'multiply');
    // The page behind the app has the theme's paper colour, too.
    root.setProperty('--paper', p.background);
    root.setProperty('--font', `'${p.font}'`);
    root.setProperty('color-scheme', p.scheme);

    if (p.decor === 'matrix') return matrixRain(p.text);
    if (p.decor === 'none') return undefined;
    const layer = p.decor === 'motifs' ? orientalFrame(p) : document.createElement('div');
    layer.setAttribute('aria-hidden', 'true');
    if (p.decor === 'constructivist') {
      Object.assign(layer.style, {
        position: 'fixed', inset: '0', pointerEvents: 'none', zIndex: '2147483646',
        backgroundImage: constructivist(p), backgroundSize: 'cover', backgroundPosition: 'center',
        opacity: String(p.scheme === 'dark' ? 0.09 : 0.11), mixBlendMode: p.scheme === 'dark' ? 'screen' : 'multiply',
      });
    }
    document.body.appendChild(layer);
    return () => layer.remove();
  }, [p]);
  return null;
}
